import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart,
  ArrowRight,
  Loader2,
  CalendarDays,
  Sparkles,
  Users,
  Clock,
  Image as ImageIcon,
  Trash2,
  X,
  Check,
  Columns2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { api, requireMember } from "@/lib/auth";
import { VENDORS } from "@/lib/vendorData";
import { PageShell } from "@/components/sections";
import { devLog } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TAB_TESTIDS = {
  vendors: "my-home-tab-vendors",
  media: "my-home-tab-media",
};

/* Resolve a slug → display info (legacy VENDORS map first, then DB list) */
const resolveVendor = (slug, dbVendors) => {
  if (VENDORS[slug]) {
    const v = VENDORS[slug];
    return {
      name: v.name,
      blurb: v.tagline,
      image: v.image || v.portrait || "",
      href: `/vendors/${slug}`,
    };
  }
  const db = dbVendors.find((v) => v.slug === slug);
  if (db) {
    return {
      name: db.company_name || slug,
      blurb: db.tagline || db.short_intro || "",
      image: (db.media_urls && db.media_urls[0]) || "",
      href: `/vendors/${slug}`,
    };
  }
  return { name: slug, blurb: "", image: "", href: `/vendors/${slug}` };
};

const resolveMediaSrc = (url) => {
  if (!url) return "";
  return url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const formatWeddingDate = (raw) => {
  if (!raw) return "—";
  // Best-effort: accept ISO date, free-form text, or "YYYY-MM-DD"
  const tryParse = new Date(raw);
  if (!Number.isNaN(tryParse.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return tryParse.toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
  return raw;
};

/* ----------------------------- Sections ---------------------------- */

const SnapshotCard = ({ icon: Icon, label, value, testId }) => (
  <div
    data-testid={testId}
    className="bg-white border border-[var(--ld-border)] p-5 sm:p-6 flex flex-col gap-3 min-h-[120px]"
  >
    <div className="inline-flex items-center gap-2 text-[var(--ld-gold-deep)]">
      <Icon size={14} strokeWidth={1.5} />
      <span className="font-accent text-[10px] tracking-[0.25em] uppercase">
        {label}
      </span>
    </div>
    <p className="font-serif-display text-xl text-[var(--ld-ink)] leading-snug break-words">
      {value || "—"}
    </p>
  </div>
);

const WeddingSnapshot = ({ couple }) => {
  if (!couple) return null;
  return (
    <section
      data-testid="my-home-snapshot"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
    >
      <SnapshotCard
        icon={CalendarDays}
        label="Wedding date"
        value={formatWeddingDate(couple.wedding_date)}
        testId="snapshot-wedding-date"
      />
      <SnapshotCard
        icon={Users}
        label="Guest count"
        value={couple.guest_count ? `${couple.guest_count}` : "—"}
        testId="snapshot-guest-count"
      />
      <SnapshotCard
        icon={Sparkles}
        label="Overall vibe"
        value={couple.overall_vibe || "—"}
        testId="snapshot-vibe"
      />
      <SnapshotCard
        icon={Heart}
        label="Wedding type"
        value={
          Array.isArray(couple.wedding_type)
            ? couple.wedding_type.join(", ") || "—"
            : couple.wedding_type || "—"
        }
        testId="snapshot-type"
      />
    </section>
  );
};

const ConsultationCard = ({ couple }) => {
  if (!couple) return null;
  const date = couple.intro_slot_date || couple.consultation_date;
  const time = couple.intro_slot_time || couple.consultation_time;
  return (
    <section
      data-testid="my-home-consultation"
      className="relative overflow-hidden bg-[var(--ld-ink)] text-[var(--ld-gold)] p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5"
    >
      <div className="absolute inset-y-0 left-0 w-1 bg-[var(--ld-gold)]/60" />
      <div>
        <span className="font-accent text-[10px] tracking-[0.28em] uppercase opacity-80">
          Your Free Consultation
        </span>
        {date ? (
          <>
            <h3 className="font-serif-display text-2xl sm:text-3xl mt-2 text-[var(--ld-gold)]">
              {formatWeddingDate(date)}
            </h3>
            {time && (
              <p className="mt-2 inline-flex items-center gap-2 text-[13px] tracking-[0.12em] uppercase opacity-90">
                <Clock size={13} strokeWidth={1.6} />
                {time}
              </p>
            )}
          </>
        ) : (
          <>
            <h3 className="font-serif-display text-2xl sm:text-3xl mt-2 text-[var(--ld-gold)]">
              Not yet booked.
            </h3>
            <p className="mt-2 text-[12.5px] leading-relaxed opacity-80 max-w-md">
              Your complimentary intro chat hasn&apos;t been scheduled yet — drop us a
              note via Contact Us and we&apos;ll find a slot that suits.
            </p>
          </>
        )}
      </div>
      <Link
        to="/contact"
        data-testid="consultation-contact-link"
        className="inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.22em] uppercase border border-[var(--ld-gold)]/60 text-[var(--ld-gold)] hover:bg-[var(--ld-gold)] hover:text-[var(--ld-ink)] transition-colors"
      >
        Reschedule
        <ArrowRight size={13} strokeWidth={1.6} />
      </Link>
    </section>
  );
};

const SavedVendorsSection = ({ slugs, dbVendors, onRemove }) => {
  if (slugs.length === 0) {
    return (
      <div
        data-testid="my-home-vendors-empty"
        className="text-center border border-[var(--ld-border)] p-12 bg-[var(--ld-ivory-warm)]"
      >
        <Heart size={28} strokeWidth={1.4} className="mx-auto text-[var(--ld-gold-deep)]" />
        <h2 className="font-serif-display text-2xl text-[var(--ld-ink)] mt-4">
          No saved vendors yet.
        </h2>
        <p className="mt-3 text-sm text-[var(--ld-ink-muted)] max-w-md mx-auto">
          Browse the directory and tap the heart on any vendor to save them here
          for later.
        </p>
        <Link
          to="/vendor-listing"
          className="inline-flex items-center gap-2 mt-6 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] px-5 py-3 hover:opacity-90"
        >
          Browse vendors
          <ArrowRight size={13} strokeWidth={1.6} />
        </Link>
      </div>
    );
  }
  return (
    <ul
      data-testid="my-home-vendors-list"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
    >
      {slugs.map((slug) => {
        const v = resolveVendor(slug, dbVendors);
        return (
          <li
            key={slug}
            data-testid={`my-home-vendor-${slug}`}
            className="relative bg-white border border-[var(--ld-border)] overflow-hidden group"
          >
            {v.image ? (
              <Link to={v.href} className="block aspect-[4/5] overflow-hidden">
                <img
                  src={resolveMediaSrc(v.image)}
                  alt={v.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
              </Link>
            ) : (
              <div className="aspect-[4/5] bg-[var(--ld-ink)]/5 flex items-center justify-center text-[var(--ld-ink-muted)] text-sm">
                No image
              </div>
            )}
            <div className="p-4">
              <h3 className="font-serif-display text-[20px] text-[var(--ld-ink)] leading-tight">
                {v.name}
              </h3>
              {v.blurb && (
                <p className="mt-1 text-[12px] text-[var(--ld-ink-muted)] line-clamp-2">
                  {v.blurb}
                </p>
              )}
              <div className="mt-4 flex items-center justify-between">
                <Link
                  to={v.href}
                  className="text-[11px] tracking-[0.18em] uppercase text-[var(--ld-ink)] underline underline-offset-4 decoration-[var(--ld-gold-deep)] hover:decoration-[var(--ld-ink)]"
                >
                  View profile
                </Link>
                <button
                  data-testid={`my-home-vendor-remove-${slug}`}
                  onClick={() => onRemove(slug)}
                  aria-label="Remove from My Home"
                  className="inline-flex items-center justify-center h-8 w-8 text-rose-500 hover:text-rose-700 transition-colors"
                >
                  <Heart size={15} strokeWidth={1.8} fill="currentColor" />
                </button>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const SavedMediaSection = ({ items, onRemove }) => {
  /* Long-press → select. Tap → open vendor profile. Second tap on a
   * selected tile → deselect. While ≥2 are selected we render a floating
   * "Compare (N)" pill that opens the CompareDrawer. */
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [compareOpen, setCompareOpen] = useState(false);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const keyOf = (item) => `${item.vendor_slug}::${item.media_url}`;
  const selectionMode = selectedKeys.size > 0;
  const selectedItems = useMemo(
    () => items.filter((it) => selectedKeys.has(keyOf(it))),
    [items, selectedKeys]
  );

  const toggleSelected = (item) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const k = keyOf(item);
      if (next.has(k)) {
        next.delete(k);
      } else if (next.size >= 4) {
        toast("Up to 4 photos can be compared at once", { duration: 2200 });
        return prev;
      } else {
        next.add(k);
      }
      return next;
    });
  };

  const startLongPress = (item) => {
    longPressTriggeredRef.current = false;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      toggleSelected(item);
      // Subtle haptic on supported devices
      try {
        if (window.navigator?.vibrate) window.navigator.vibrate(15);
      } catch (e) {
        devLog("ld_myhome vibrate unsupported:", e?.message);
      }
    }, 420);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleTileClick = (e, item) => {
    // If a long-press fired, swallow the click so we don't also navigate.
    if (longPressTriggeredRef.current) {
      e.preventDefault();
      longPressTriggeredRef.current = false;
      return;
    }
    // If we're already in selection mode, plain clicks toggle selection
    // instead of navigating away.
    if (selectionMode) {
      e.preventDefault();
      toggleSelected(item);
    }
  };

  if (items.length === 0) {
    return (
      <div
        data-testid="my-home-media-empty"
        className="text-center border border-[var(--ld-border)] p-12 bg-[var(--ld-ivory-warm)]"
      >
        <ImageIcon size={28} strokeWidth={1.4} className="mx-auto text-[var(--ld-gold-deep)]" />
        <h2 className="font-serif-display text-2xl text-[var(--ld-ink)] mt-4">
          No saved photos or videos yet.
        </h2>
        <p className="mt-3 text-sm text-[var(--ld-ink-muted)] max-w-md mx-auto">
          Open any vendor profile and tap the heart on a specific photo or video
          to save it here for inspiration.
        </p>
        <Link
          to="/vendor-listing"
          className="inline-flex items-center gap-2 mt-6 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] px-5 py-3 hover:opacity-90"
        >
          Browse vendors
          <ArrowRight size={13} strokeWidth={1.6} />
        </Link>
      </div>
    );
  }
  return (
    <>
      {/* Pro tip banner */}
      <div
        data-testid="my-home-protip"
        className="mb-5 flex items-center gap-3 border border-[var(--ld-gold)]/40 bg-[var(--ld-ivory-warm)] px-4 py-3 text-[12.5px] text-[var(--ld-ink)]"
      >
        <Sparkles size={14} strokeWidth={1.6} className="shrink-0 text-[var(--ld-gold-deep)]" />
        <p className="leading-relaxed">
          <span className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-gold-deep)] mr-2">
            Pro tip
          </span>
          Long-press any image to start comparing side-by-side — pick 2–4 and tap{" "}
          <span className="italic">Compare</span>.
        </p>
      </div>

      <ul
        data-testid="my-home-media-list"
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3"
      >
        {items.map((item, i) => {
          const src = resolveMediaSrc(item.media_url);
          const isVideo = item.media_type === "video";
          const k = keyOf(item);
          const isSelected = selectedKeys.has(k);
          return (
            <li
              key={k}
              data-testid={`my-home-media-${i}`}
              data-selected={isSelected ? "true" : undefined}
              className={
                "relative group overflow-hidden bg-[var(--ld-ink)]/5 aspect-square select-none transition-all " +
                (isSelected
                  ? "border-2 border-[var(--ld-gold-deep)] shadow-[0_8px_24px_rgba(193,154,90,0.25)]"
                  : "border border-[var(--ld-border)]")
              }
              onMouseDown={() => startLongPress(item)}
              onMouseUp={cancelLongPress}
              onMouseLeave={cancelLongPress}
              onTouchStart={() => startLongPress(item)}
              onTouchEnd={cancelLongPress}
              onTouchMove={cancelLongPress}
              onContextMenu={(e) => e.preventDefault()}
            >
              <Link
                to={`/vendors/${item.vendor_slug}`}
                onClick={(e) => handleTileClick(e, item)}
                className="block w-full h-full"
                draggable={false}
              >
                {isVideo ? (
                  <video
                    src={src}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="block w-full h-full object-cover pointer-events-none"
                  />
                ) : (
                  <img
                    src={src}
                    alt={item.vendor_slug}
                    loading="lazy"
                    draggable={false}
                    className="block w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04] pointer-events-none"
                  />
                )}
                <div
                  aria-hidden
                  className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-3 py-3"
                >
                  <span className="font-accent text-[9px] tracking-[0.22em] uppercase text-white/90">
                    {item.vendor_slug}
                  </span>
                </div>
              </Link>

              {/* Selection check badge */}
              {isSelected && (
                <span
                  aria-hidden
                  data-testid={`my-home-media-selected-${i}`}
                  className="absolute top-2 left-2 inline-flex h-6 w-6 items-center justify-center bg-[var(--ld-gold-deep)] text-white rounded-full shadow"
                >
                  <Check size={13} strokeWidth={2.4} />
                </span>
              )}

              <button
                data-testid={`my-home-media-remove-${i}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onRemove(item);
                }}
                aria-label="Remove from My Home"
                className="absolute top-2 right-2 inline-flex h-8 w-8 items-center justify-center bg-black/60 text-white/90 backdrop-blur-sm hover:bg-black/85 transition-colors"
              >
                <Trash2 size={13} strokeWidth={1.7} />
              </button>
            </li>
          );
        })}
      </ul>

      {/* Floating compare bar */}
      {selectionMode && (
        <div
          data-testid="my-home-compare-bar"
          className="fixed left-1/2 -translate-x-1/2 bottom-6 z-40 flex items-center gap-3 px-3 py-2 bg-[var(--ld-ink)] text-[var(--ld-gold)] shadow-[0_20px_50px_rgba(44,42,41,0.35)] border border-[var(--ld-gold-deep)]/40"
        >
          <span className="font-accent text-[11px] tracking-[0.22em] uppercase px-2">
            {selectedKeys.size} selected
          </span>
          <button
            data-testid="my-home-compare-clear"
            onClick={() => setSelectedKeys(new Set())}
            className="inline-flex h-8 w-8 items-center justify-center text-[var(--ld-gold)]/70 hover:text-[var(--ld-gold)]"
            aria-label="Clear selection"
          >
            <X size={14} strokeWidth={1.7} />
          </button>
          <button
            data-testid="my-home-compare-open"
            disabled={selectedKeys.size < 2}
            onClick={() => setCompareOpen(true)}
            className={
              "inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors " +
              (selectedKeys.size < 2
                ? "border-[var(--ld-gold)]/30 text-[var(--ld-gold)]/40 cursor-not-allowed"
                : "border-[var(--ld-gold)] text-[var(--ld-gold)] hover:bg-[var(--ld-gold)] hover:text-[var(--ld-ink)]")
            }
          >
            <Columns2 size={13} strokeWidth={1.7} />
            Compare {selectedKeys.size >= 2 ? `(${selectedKeys.size})` : ""}
          </button>
        </div>
      )}

      <CompareDrawer
        open={compareOpen}
        items={selectedItems}
        onClose={() => setCompareOpen(false)}
      />
    </>
  );
};

/* --------------------- Side-by-side compare modal ----------------- */
const CompareDrawer = ({ open, items, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  const n = items.length;
  const gridCols =
    n <= 1
      ? "grid-cols-1"
      : n === 2
      ? "grid-cols-1 sm:grid-cols-2"
      : "grid-cols-2 lg:grid-cols-3";

  return (
    <div
      data-testid="my-home-compare-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Compare saved media"
      className="fixed inset-0 z-50 bg-[var(--ld-ink)]/95 backdrop-blur-sm flex flex-col"
    >
      <div className="flex items-center justify-between px-5 sm:px-8 py-4 border-b border-white/10">
        <div>
          <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-[var(--ld-gold)]">
            Side-by-side
          </span>
          <h2 className="font-serif-display text-xl sm:text-2xl text-white mt-1">
            Compare your shortlist
          </h2>
        </div>
        <button
          data-testid="my-home-compare-close"
          onClick={onClose}
          aria-label="Close"
          className="inline-flex h-10 w-10 items-center justify-center text-white/80 hover:text-white border border-white/15"
        >
          <X size={16} strokeWidth={1.6} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 sm:p-8">
        <ul
          data-testid="compare-grid"
          className={`grid ${gridCols} gap-3 sm:gap-4 max-w-6xl mx-auto`}
        >
          {items.map((it, i) => {
            const src = resolveMediaSrc(it.media_url);
            const isVideo = it.media_type === "video";
            return (
              <li
                key={`${it.vendor_slug}-${it.media_url}`}
                data-testid={`compare-tile-${i}`}
                className="relative overflow-hidden border border-white/10 bg-black/40"
              >
                <div className="aspect-[3/4] w-full">
                  {isVideo ? (
                    <video
                      src={src}
                      controls
                      muted
                      playsInline
                      className="block w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={src}
                      alt={it.vendor_slug}
                      className="block w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="px-3 py-2 bg-black/55 text-[var(--ld-gold)]">
                  <Link
                    to={`/vendors/${it.vendor_slug}`}
                    onClick={onClose}
                    className="inline-flex items-center gap-2 font-accent text-[10px] tracking-[0.22em] uppercase hover:text-white"
                  >
                    {it.vendor_slug}
                    <ArrowRight size={11} strokeWidth={1.6} />
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

/* ----------------------------- Page -------------------------------- */

const MyHome = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("vendors"); // "vendors" | "media"
  const [slugs, setSlugs] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [dbVendors, setDbVendors] = useState([]);
  const [couple, setCouple] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { user, redirect } = await requireMember();
      if (cancelled) return;
      if (!user) {
        navigate("/signin?next=/my-home", { replace: true });
        return;
      }
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }
      // Page is couples-only + admin (admin gets read-only view; vendors
      // are bounced to homepage — they don't have a "My Home").
      if (user.role !== "couple" && user.role !== "admin") {
        navigate("/", { replace: true });
        return;
      }
      setMe(user);
      try {
        const [favRes, mediaRes, vendorsRes, coupleRes] = await Promise.all([
          axios.get(`${API}/favorites`, { params: { email: user.email } }),
          axios.get(`${API}/favorites/media`, { params: { email: user.email } }),
          axios.get(`${API}/vendors`),
          api.get(`/couple-signups/me`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setSlugs(favRes.data.slugs || []);
        setMediaItems(mediaRes.data.items || []);
        setDbVendors(vendorsRes.data || []);
        setCouple(coupleRes.data);
      } catch (e) {
        devLog("ld_myhome load failed:", e?.message);
        if (!cancelled) toast("Couldn't load your home page", { duration: 2400 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleRemoveVendor = async (slug) => {
    try {
      await axios.post(`${API}/favorites/toggle`, {
        email: me.email,
        vendor_slug: slug,
      });
      setSlugs((s) => s.filter((x) => x !== slug));
      toast("Removed from My Home", { duration: 1600 });
    } catch (e) {
      devLog("ld_myhome removeVendor failed:", e?.message);
      toast("Couldn't update favourite", { duration: 2200 });
    }
  };

  const handleRemoveMedia = async (item) => {
    try {
      await axios.post(`${API}/favorites/media/toggle`, {
        email: me.email,
        vendor_slug: item.vendor_slug,
        media_url: item.media_url,
        media_type: item.media_type,
      });
      setMediaItems((s) =>
        s.filter(
          (i) => !(i.vendor_slug === item.vendor_slug && i.media_url === item.media_url)
        )
      );
      toast("Removed from My Home", { duration: 1600 });
    } catch (e) {
      devLog("ld_myhome removeMedia failed:", e?.message);
      toast("Couldn't update favourite", { duration: 2200 });
    }
  };

  const counts = useMemo(
    () => ({ vendors: slugs.length, media: mediaItems.length }),
    [slugs, mediaItems]
  );

  return (
    <PageShell testId="my-home-page">
      <section className="px-6 md:px-12 py-12 md:py-20">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div>
            <span className="font-accent text-[11px] tracking-[0.25em] uppercase text-[var(--ld-gold-deep)]">
              My Home
            </span>
            <h1
              data-testid="my-home-heading"
              className="font-serif-display mt-3 text-3xl sm:text-4xl md:text-5xl text-[var(--ld-ink)] font-medium leading-tight"
            >
              Your wedding,{" "}
              <span className="italic">all in one place.</span>
            </h1>
            {me && (
              <p className="mt-3 text-sm text-[var(--ld-ink-muted)]">
                Signed in as{" "}
                <span className="text-[var(--ld-ink)] font-medium">{me.email}</span>
              </p>
            )}
          </div>

          {loading && (
            <div className="mt-10 flex items-center gap-3 text-[var(--ld-ink-muted)]">
              <Loader2 size={16} className="animate-spin" />
              <span>Loading your home page…</span>
            </div>
          )}

          {!loading && (
            <>
              {/* Wedding snapshot */}
              {couple && (
                <div className="mt-10">
                  <WeddingSnapshot couple={couple} />
                </div>
              )}

              {/* Consultation */}
              {couple && (
                <div className="mt-6">
                  <ConsultationCard couple={couple} />
                </div>
              )}

              {/* No couple form on file (admin viewing, or couple skipped) */}
              {!couple && me?.role === "couple" && (
                <div
                  data-testid="my-home-no-couple-form"
                  className="mt-10 border border-[var(--ld-border)] p-6 bg-white/60 text-[14px] text-[var(--ld-ink-muted)]"
                >
                  Complete your{" "}
                  <Link
                    to="/couple/signup"
                    className="text-[var(--ld-gold-deep)] hover:underline"
                  >
                    couple form
                  </Link>{" "}
                  to see your wedding snapshot and consultation slot here.
                </div>
              )}

              {/* Tabs */}
              <div
                role="tablist"
                aria-label="My Home tabs"
                className="mt-12 flex items-center gap-1 border-b border-[var(--ld-border)]"
              >
                <button
                  role="tab"
                  aria-selected={tab === "vendors"}
                  data-testid={TAB_TESTIDS.vendors}
                  onClick={() => setTab("vendors")}
                  className={
                    "relative px-5 py-3 text-[11px] tracking-[0.22em] uppercase transition-colors " +
                    (tab === "vendors"
                      ? "text-[var(--ld-ink)]"
                      : "text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]")
                  }
                >
                  Saved Vendors
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] bg-[var(--ld-gold)]/20 text-[var(--ld-gold-deep)]">
                    {counts.vendors}
                  </span>
                  {tab === "vendors" && (
                    <span
                      aria-hidden
                      className="absolute left-0 right-0 -bottom-px h-[2px] bg-[var(--ld-gold-deep)]"
                    />
                  )}
                </button>
                <button
                  role="tab"
                  aria-selected={tab === "media"}
                  data-testid={TAB_TESTIDS.media}
                  onClick={() => setTab("media")}
                  className={
                    "relative px-5 py-3 text-[11px] tracking-[0.22em] uppercase transition-colors " +
                    (tab === "media"
                      ? "text-[var(--ld-ink)]"
                      : "text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]")
                  }
                >
                  Saved Photos & Videos
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] bg-[var(--ld-gold)]/20 text-[var(--ld-gold-deep)]">
                    {counts.media}
                  </span>
                  {tab === "media" && (
                    <span
                      aria-hidden
                      className="absolute left-0 right-0 -bottom-px h-[2px] bg-[var(--ld-gold-deep)]"
                    />
                  )}
                </button>
              </div>

              <div className="mt-10">
                {tab === "vendors" ? (
                  <SavedVendorsSection
                    slugs={slugs}
                    dbVendors={dbVendors}
                    onRemove={handleRemoveVendor}
                  />
                ) : (
                  <SavedMediaSection
                    items={mediaItems}
                    onRemove={handleRemoveMedia}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default MyHome;
